// ─────────────────────────────────────────────────────────────
// Helpers — Pure utility functions (no Firebase, no DOM)
// ─────────────────────────────────────────────────────────────

const MONTHS = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];

/** Format YYYY-MM-DD to "15 jan" */
export function fmt(d: string): string {
  if (!d) return '—';
  const [, m, day] = d.split('-');
  return `${parseInt(day)} ${MONTHS[parseInt(m) - 1]}`;
}

/** Format number to "R$ 1.234,56" — preserves cents */
export function fmtR(n: number): string {
  return 'R$ ' + (n || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Format number to masked string "1.234,56" */
export function fmtMask(n: number): string {
  return (n || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Parse masked string "1.234,56" to number */
export function parseMask(v: string): number {
  return parseFloat((v || '0').replace(/\./g, '').replace(',', '.')) || 0;
}

/** Difference in days between two YYYY-MM-DD strings */
export function diffDays(a: string, b: string): number {
  if (!a || !b) return 0;
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86_400_000);
}

/** Convert Firebase object to typed array with id injected */
export function toArr<T extends object>(
  obj: Record<string, T> | null | undefined
): (T & { id: string })[] {
  if (!obj) return [];
  return Object.entries(obj).map(([id, v]) => ({ ...v, id }));
}

/** Generate a short unique id */
export function uid(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

/** Get initials from a name */
export function initials(name: string): string {
  return (name || '?')
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** Avatar background color palette */
const AVATAR_COLORS = [
  '#00A3E0','#EF3340','#1E9E5E','#D97706',
  '#6D5FD5','#0891B2','#DC2626','#059669',
];
export function avatarColor(i: number): string {
  return AVATAR_COLORS[i % AVATAR_COLORS.length];
}

/** Status badge HTML */
export function statusBadge(status: string): string {
  const map: Record<string, string> = {
    confirmado: 'b-ok',
    pendente:   'b-warn',
    cancelado:  'b-danger',
  };
  const cls   = map[status] || 'b-info';
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return `<span class="badge ${cls}">${label}</span>`;
}

/** Password strength validator */
export interface PasswordStrength {
  len: boolean; upper: boolean; lower: boolean; num: boolean; spec: boolean; valid: boolean;
}
export function passwordStrength(pwd: string): PasswordStrength {
  const len   = pwd.length >= 8;
  const upper = /[A-Z]/.test(pwd);
  const lower = /[a-z]/.test(pwd);
  const num   = /[0-9]/.test(pwd);
  const spec  = /[^A-Za-z0-9]/.test(pwd);
  return { len, upper, lower, num, spec, valid: len && upper && lower && num && spec };
}

/** Handle budget mask input — keeps cents */
export function onBudgetInput(el: HTMLInputElement): void {
  const raw   = el.value.replace(/\D/g, '');
  const cents = parseInt(raw || '0', 10);
  el.value    = (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
}

/** Sanitize string for safe innerHTML insertion — prevents XSS */
export function sanitize(str: string): string {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/** Get element value by id */
export function g(id: string): string {
  const el = document.getElementById(id) as HTMLInputElement | null;
  return el ? el.value.trim() : '';
}

/** Get element numeric value */
export function gf(id: string): number {
  const el = document.getElementById(id) as HTMLInputElement | null;
  return el ? parseMask(el.value) : 0;
}

/** Doc file type icon */
export function docIcon(name: string): string {
  const ext = (name || '').split('.').pop()?.toLowerCase() || '';
  if (['jpg','jpeg','png','webp','gif'].includes(ext)) return '🖼';
  if (ext === 'pdf') return '📄';
  if (['doc','docx'].includes(ext)) return '📝';
  return '📎';
}

/** Validate required fields — throws with user-friendly message */
export function requireFields(fields: { id: string; label: string }[]): void {
  for (const { id, label } of fields) {
    const val = g(id);
    if (!val) throw new Error(`O campo "${label}" é obrigatório.`);
  }
}
